import React, { forwardRef } from 'react';
import { Link } from 'react-router-dom';
import './Button.css';

interface ButtonProps extends React.ButtonHTMLAttributes<HTMLButtonElement> {
  variant?: 'primary' | 'secondary' | 'danger' | 'ghost' | 'warning';
  size?: 'sm' | 'md' | 'lg';
  fullWidth?: boolean;
  loading?: boolean;
  iconLeft?: React.ReactNode;
  iconRight?: React.ReactNode;
  /** 'a' is a real anchor (full page load); 'link' routes in-app. */
  as?: 'button' | 'a' | 'link';
  href?: string;
  /** Required when as="link". Same value you'd give a React Router <Link>. */
  to?: string;
}

const Button = forwardRef<HTMLButtonElement | HTMLAnchorElement, ButtonProps>(
  (
    {
      variant = 'primary',
      size = 'md',
      fullWidth = false,
      loading = false,
      iconLeft,
      iconRight,
      as = 'button',
      href,
      to,
      disabled,
      className = '',
      children,
      ...props
    },
    ref
  ) => {
    const isDisabled = disabled || loading;
    const classNames = [
      'btn',
      `btn--${variant}`,
      `btn--${size}`,
      fullWidth ? 'btn--block' : '',
      loading ? 'btn--loading' : '',
      isDisabled ? 'btn--disabled' : '',
      className,
    ]
      .filter(Boolean)
      .join(' ');

    const content = (
      <>
        {loading && (
          <span className="btn__spinner" aria-hidden="true">
            <span className="btn__spinner-circle" />
          </span>
        )}
        {iconLeft && !loading && (
          <span className="btn__icon btn__icon--left">{iconLeft}</span>
        )}
        <span className="btn__content">{children}</span>
        {iconRight && (
          <span className="btn__icon btn__icon--right">{iconRight}</span>
        )}
      </>
    );

    // Disabled has no meaning for a route change, so fall back to a plain
    // span-less button rather than rendering a dead <Link>.
    if (as === 'link' && to && !isDisabled) {
      return (
        <Link
          ref={ref as React.Ref<HTMLAnchorElement>}
          to={to}
          className={classNames}
          {...(props as React.AnchorHTMLAttributes<HTMLAnchorElement>)}
        >
          {content}
        </Link>
      );
    }

    if (as === 'a' && href) {
      return (
        <a
          ref={ref as React.Ref<HTMLAnchorElement>}
          href={isDisabled ? undefined : href}
          className={classNames}
          aria-disabled={isDisabled}
          tabIndex={isDisabled ? -1 : undefined}
          {...(props as React.AnchorHTMLAttributes<HTMLAnchorElement>)}
        >
          {content}
        </a>
      );
    }

    return (
      <button
        ref={ref as React.Ref<HTMLButtonElement>}
        type={(props as React.ButtonHTMLAttributes<HTMLButtonElement>).type || 'button'}
        disabled={isDisabled}
        className={classNames}
        {...(props as React.ButtonHTMLAttributes<HTMLButtonElement>)}
      >
        {content}
      </button>
    );
  }
);

Button.displayName = 'Button';

export default Button;
